import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from "rxjs/operators";

import {
  InputActionResult,
  MapActionData,
  PlacePrediction,
  SearchDisplayMode,
  SearchInputState,
  SearchInputType,
  SearchInputTypeUtils,
  SearchSuggestion,
  TypeDetectionResult,
} from "../../model";

import { SearchInputDetector, SearchModeCoordinator } from "./validation";
import { SearchHistoryService } from "./search-history.service";
import { SearchPlacesAutocompleteService } from "./search-places-autocomplete.service";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";

/**
 * Enhanced search input service with unified flow
 *
 * MAIN FLOW:
 * 1. User types → processInput() with debounce → detects type & shows suggestions
 * 2. User selects suggestion → selectSuggestion() → executes action immediately
 * 3. User presses Enter/Search → executeInputSearch() → re-detects type & executes
 *
 * KEY FEATURES:
 * - Unified state management (single SearchInputState)
 * - Real-time detection for coordinates/WKT
 * - Google Places integration for addresses
 * - History filtering and storage
 * - Type-based action execution
 * - Address confidence upgrading
 */
@Injectable({
  providedIn: "root",
})
export class SearchInputService {
  private readonly MAX_SUGGESTIONS = 6;

  private readonly DEBOUNCE_TIME_MS = 350;

  // ========================================
  // DEPENDENCIES
  // ========================================

  private readonly inputDetector = inject(SearchInputDetector);

  private readonly searchHistory = inject(SearchHistoryService);

  private readonly placesService = inject(SearchPlacesAutocompleteService);

  private readonly modeCoordinator = inject(SearchModeCoordinator);

  // ========================================
  // UNIFIED STATE MANAGEMENT
  // ========================================

  /** Unified input state - single source of truth */
  private readonly inputStateSubject = new BehaviorSubject<SearchInputState>({
    inputText: "",
    detectedType: SearchInputType.TEXT,
    confidence: 0,
    isValid: false,
    loading: false,
    lastAnalyzed: 0,
    currentSuggestions: [],
  });

  /** Subject for canceling pending requests */
  private readonly cancelRequests$ = new Subject<void>();

  /** Public observables */
  public readonly inputState$ = this.inputStateSubject.asObservable();

  public readonly suggestions$ = this.inputState$.pipe(map((state) => state.currentSuggestions));

  public readonly loading$ = this.inputState$.pipe(map((state) => state.loading));

  // ========================================
  // MAIN PUBLIC METHODS - NEW UNIFIED FLOW
  // ========================================

  /**
   * Process input with debounce and generate suggestions
   * Main method called on every keystroke
   *
   * @param input Current input text
   * @param displayMode Current display mode for validation
   */
  processInput(input: string, displayMode: SearchDisplayMode): void {
    // Cancel previous requests
    this.cancelRequests$.next();

    // Handle empty input
    if (!input?.trim()) {
      const historySuggestions = this.getHistorySuggestions(input, displayMode);
      this.updateState({
        inputText: "",
        detectedType: SearchInputType.TEXT,
        confidence: 0,
        isValid: true,
        loading: false,
        currentSuggestions: historySuggestions,
        lastAnalyzed: Date.now(),
      });
      return;
    }

    // Start loading state
    this.updateState({
      inputText: input,
      loading: true,
      lastAnalyzed: Date.now(),
    });

    // Process with debounce through internal observable
    this.processInputWithDebounce(input, displayMode).subscribe();
  }

  /**
   * Execute search based on current input state
   * Used when user presses Enter or clicks Search button
   * Re-detects type to ensure accuracy
   *
   * @returns Promise of action result
   */
  async executeInputSearch(): Promise<InputActionResult> {
    // Cancel any debounced suggestion generation so stale `inputState$` emissions cannot reopen the overlay
    this.cancelRequests$.next();

    const currentState = this.getCurrentState();
    const input = currentState.inputText.trim();

    if (!input) {
      return {
        success: false,
        actionType: "server_search",
        message: "No input provided",
      };
    }

    // Re-detect type to ensure accuracy (especially for address high-confidence)
    const detectionResult = this.detectInput(input);

    // Clear suggestions and loading state so the `inputState$` subscription does not reopen the overlay
    this.updateState({ detectedType: detectionResult.inputType, currentSuggestions: [], loading: false });

    // Execute action based on final type
    const result = await this.executeActionByType(detectionResult.inputType, input, currentState);

    // Save to history if applicable
    if (result.success && SearchInputTypeUtils.shouldSaveToHistory(detectionResult.inputType)) {
      this.searchHistory.addTypedSearch(result.message, detectionResult.inputType, detectionResult.confidence, null);
    }

    return result;
  }

  /**
   * Select a suggestion and execute its action
   * Uses suggestion type directly without re-detection
   *
   * @param suggestion Selected suggestion
   * @returns Promise of action result
   */
  async selectSuggestion(suggestion: SearchSuggestion): Promise<InputActionResult> {
    // Cancel any debounced suggestion generation so stale `inputState$` emissions cannot reopen the overlay
    this.cancelRequests$.next();

    // Clear suggestions and loading state so the `inputState$` subscription does not reopen the overlay
    this.updateState({
      inputText: suggestion.text,
      detectedType: suggestion.type,
      confidence: suggestion.confidence,
      currentSuggestions: [],
      loading: false,
    });

    // Execute action based on suggestion type
    const result = await this.executeActionByType(suggestion.type, suggestion.text, undefined, suggestion);

    // Save to history if applicable
    if (result.success && SearchInputTypeUtils.shouldSaveToHistory(suggestion.type)) {
      this.searchHistory.addTypedSearch(suggestion.text, suggestion.type, suggestion.confidence, suggestion.data);
    }

    return result;
  }

  /**
   * Clear input state and cancel pending operations
   */
  clearState(): void {
    this.cancelRequests$.next();
    this.inputStateSubject.next({
      inputText: "",
      detectedType: SearchInputType.TEXT,
      confidence: 0,
      isValid: false,
      loading: false,
      lastAnalyzed: 0,
      currentSuggestions: [],
    });
  }

  /**
   * Cancel any pending debounced suggestion generation.
   * Called on blur so that late-arriving suggestions cannot reopen the overlay.
   */
  cancelPendingSuggestions(): void {
    this.cancelRequests$.next();
    this.updateState({ loading: false });
  }

  /**
   * Get current input state (public accessor)
   */
  getCurrentState(): SearchInputState {
    return this.inputStateSubject.value;
  }

  // ========================================
  // PRIVATE PROCESSING METHODS
  // ========================================

  /**
   * Internal debounced processing method
   */
  private processInputWithDebounce(input: string, displayMode: SearchDisplayMode): Observable<SearchSuggestion[]> {
    return of(input).pipe(
      debounceTime(this.DEBOUNCE_TIME_MS),
      distinctUntilChanged(),
      switchMap(() => this.generateSuggestions(input, displayMode)),
      takeUntil(this.cancelRequests$),
      catchError(() => {
        this.updateState({
          loading: false,
          currentSuggestions: this.getHistorySuggestions(input, displayMode),
        });
        return of([]);
      }),
    );
  }

  detectInput(input: string): TypeDetectionResult {
    return this.inputDetector.detectInput(input);
  }

  /**
   * Main suggestion generation method
   */
  private generateSuggestions(input: string, displayMode: SearchDisplayMode): Observable<SearchSuggestion[]> {
    // 1. Detect input type
    const detectionResult = this.detectInput(input);

    // 2. Validate against display mode
    const validation = this.modeCoordinator.canSuggest(detectionResult.inputType, displayMode);

    if (!validation.valid) {
      this.updateState({
        detectedType: detectionResult.inputType,
        confidence: detectionResult.confidence * 100,
        isValid: false,
        loading: false,
        currentSuggestions: [],
      });
      return of([]);
    }

    // 3. Handle immediate types (coordinate/WKT)
    if (SearchInputTypeUtils.isRealTimeGenerated(detectionResult.inputType)) {
      return this.handleImmediateDetection(detectionResult, input, displayMode);
    }

    // 4. Handle address/text types with Google Places
    return this.handleAddressDetection(input, displayMode);
  }

  /**
   * Handle coordinate and WKT detection (immediate suggestions)
   */
  private handleImmediateDetection(
    detection: TypeDetectionResult,
    input: string,
    displayMode: SearchDisplayMode,
  ): Observable<SearchSuggestion[]> {
    // Create immediate suggestion
    const suggestion = this.createImmediateSuggestion(detection, input);

    // Get history suggestions
    const historySuggestions = this.getHistorySuggestions(input, displayMode);

    // Combine suggestions
    const allSuggestions = suggestion ? [suggestion, ...historySuggestions] : historySuggestions;
    const finalSuggestions = allSuggestions.slice(0, this.MAX_SUGGESTIONS);

    this.updateState({
      detectedType: detection.inputType,
      confidence: detection.confidence * 100,
      isValid: true,
      loading: false,
      currentSuggestions: finalSuggestions,
    });

    return of(finalSuggestions);
  }

  /**
   * Handle address detection with Google Places
   */
  private handleAddressDetection(input: string, displayMode: SearchDisplayMode): Observable<SearchSuggestion[]> {
    // Check if Places API is allowed
    const placesValidation = this.modeCoordinator.canCallPlacesApi(displayMode);

    if (!placesValidation.valid) {
      // Only use history if Places API not allowed
      const historySuggestions = this.getHistorySuggestions(input, displayMode);
      this.updateState({
        detectedType: SearchInputType.TEXT,
        confidence: 80,
        isValid: true,
        loading: false,
        currentSuggestions: historySuggestions,
      });
      return of(historySuggestions);
    }

    // Call Google Places API
    return this.placesService.suggestFromPlaces(input).pipe(
      map((predictions) => {
        let finalType: SearchInputType = SearchInputType.TEXT;
        let confidence = 80;
        let placeSuggestions: SearchSuggestion[] = [];
        let highConfidenceAddress: SearchSuggestion | undefined;

        // Process Google Places results
        if (predictions?.length > 0) {
          const addressConfidence = this.calculateAddressConfidence(predictions, input);

          if (addressConfidence > 0.75) {
            // High confidence address - upgrade type
            finalType = SearchInputType.GOOGLE_PLACE;
            confidence = addressConfidence * 100;

            placeSuggestions = predictions
              .slice(0, 3)
              .map((prediction, index) => this.createPlaceSuggestion(prediction, index));

            highConfidenceAddress = placeSuggestions[0];
          } else {
            // Low confidence - keep as text but add place suggestions
            placeSuggestions = predictions
              .slice(0, 2)
              .map((prediction, index) => this.createPlaceSuggestion(prediction, index));
          }
        }

        // Get history suggestions
        const historySuggestions = this.getHistorySuggestions(input, displayMode);
        // 1. Merge arrays (if you have separate arrays)
        const merged = [...historySuggestions, ...placeSuggestions];

        // 2. Sort
        const sorted = this.sortMixedSuggestions(merged, input);
        // Combine all suggestions
        const allSuggestions = sorted.slice(0, this.MAX_SUGGESTIONS);

        this.updateState({
          detectedType: finalType,
          confidence,
          isValid: true,
          loading: false,
          currentSuggestions: allSuggestions,
          highConfidenceAddress,
        });

        return allSuggestions;
      }),
      catchError((error) => {
        console.error("❌ Error in address detection:", error);

        // Fallback to history suggestions
        const historySuggestions = this.getHistorySuggestions(input, displayMode);
        this.updateState({
          detectedType: SearchInputType.TEXT,
          confidence: 80,
          isValid: true,
          loading: false,
          currentSuggestions: historySuggestions,
        });

        return of(historySuggestions);
      }),
    );
  }

  /**
   * Sorts suggestions by text similarity, usage, and recency.
   * Ignores type and confidence.
   * Penalizes missing usageCount and lastUsed.
   * @param {any[]} suggestions - Array of suggestion objects.
   * @param {string} searchTerm - The current search term.
   * @returns {any[]} Sorted array of suggestions.
   */
  sortMixedSuggestions(suggestions: any[], searchTerm: string): any[] {
    const term = searchTerm.toLowerCase();

    // Helper to calculate text similarity score
    function getSimilarity(text: string | undefined): number {
      if (!text) return 0;
      const lower = text.toLowerCase();
      if (lower === term) return 1000; // exact match
      if (lower.startsWith(term)) return 900; // starts with
      if (lower.includes(term)) return 800; // contains
      return 0;
    }

    return [...suggestions].sort((a, b) => {
      const aSim = getSimilarity(a.text);
      const bSim = getSimilarity(b.text);
      if (aSim !== bSim) return bSim - aSim;

      const aUsage = typeof a.usageCount === "number" ? a.usageCount : -100;
      const bUsage = typeof b.usageCount === "number" ? b.usageCount : -100;
      if (aUsage !== bUsage) return bUsage - aUsage;

      const aRecency = a.lastUsed ? new Date(a.lastUsed).getTime() : -1000000000;
      const bRecency = b.lastUsed ? new Date(b.lastUsed).getTime() : -1000000000;
      if (aRecency !== bRecency) return bRecency - aRecency;

      return (a.text ?? "").localeCompare(b.text ?? "");
    });
  }

  // ========================================
  // ACTION EXECUTION METHODS
  // ========================================

  /**
   * Execute action based on input type
   */
  async executeActionByType(
    type: SearchInputType,
    input: string,
    state?: SearchInputState,
    suggestion?: SearchSuggestion,
  ): Promise<InputActionResult> {
    switch (type) {
      case SearchInputType.COORDINATE:
        return this.executeCoordinateAction(input, suggestion);

      case SearchInputType.WKT_GEOMETRY:
        return this.executeGeometryAction(input, suggestion);

      case SearchInputType.GOOGLE_PLACE:
        return this.executeAddressAction(input, suggestion);

      case SearchInputType.TEXT:
      default:
        return this.executeTextSearchAction(input);
    }
  }

  /**
   * Execute coordinate action - center map immediately
   */
  private executeCoordinateAction(input: string, suggestion?: SearchSuggestion): Promise<InputActionResult> {
    const coords = suggestion?.data?.coordinates || this.parseCoordinatesFromText(input);

    if (!coords) {
      return Promise.resolve({
        success: false,
        actionType: "map_action",
        message: "Invalid coordinates",
      });
    }

    try {
      const data: MapActionData = {
        coordinates: coords,
        type: "move",
      };
      return Promise.resolve({
        success: true,
        actionType: "map_action",
        message: `Map centered on coordinates: ${coords.lat}, ${coords.lng}`,
        data: data,
      });
    } catch (error) {
      console.error("❌ Error centering map:", error);
      return Promise.resolve({
        success: false,
        actionType: "map_action",
        message: "Failed to center map",
      });
    }
  }

  /**
   * Execute geometry action - fit bounds to geometry
   */
  private executeGeometryAction(input: string, suggestion?: SearchSuggestion): Promise<InputActionResult> {
    let wkt, boundingBox;
    if (suggestion?.data?.geometry) {
      wkt = suggestion?.data?.geometry?.wkt || input;
      boundingBox = suggestion?.data?.geometry?.boundingBox;
    } else {
      wkt = input;
      boundingBox = GeoService.wktPolygonToBbox(wkt, false);
    }

    if (wkt) {
      try {
        const data: MapActionData = {
          geometry: {
            wkt,
            boundingBox,
          },
          type: "fit",
        };
        return Promise.resolve({
          success: true,
          actionType: "map_action",
          message: "Map fitted to geometry bounds",
          data: data,
        });
      } catch (error) {
        console.error("❌ Error fitting to bounds:", error);
      }
    }

    return Promise.resolve({
      success: false,
      actionType: "map_action",
      message: "Geometry bounding box calculation not implemented",
    });
  }

  /**
   * Execute address action - get place details and center map
   */
  private async executeAddressAction(input: string, suggestion?: SearchSuggestion): Promise<InputActionResult> {
    const placeId = suggestion?.data?.address?.placeId;
    const location = suggestion?.data?.address?.location;
    // If we have placeId, get place details
    if (placeId) {
      try {
        const placeDetails = await this.placesService.getPlaceDetails(placeId).toPromise();
        if (placeDetails) {
          let data: MapActionData;

          if (placeDetails?.placeResult.geometry.viewport) {
            const bbox = placeDetails.placeResult.geometry.viewport.toJSON();
            data = {
              geometry: {
                boundingBox: [bbox.west, bbox.south, bbox.east, bbox.north],
              },
              type: "fit",
            };
          } else if (placeDetails?.placeResult.geometry.location) {
            data = {
              coordinates: {
                lat: placeDetails?.placeResult.geometry.location.lat(),
                lng: placeDetails?.placeResult.geometry.location.lng(),
              },
              type: "move",
            };
          } else if (placeDetails?.location) {
            data = {
              coordinates: {
                lat: location[0],
                lng: location[1],
              },
              type: "move",
            };
          }
          return {
            success: true,
            actionType: "place_lookup",
            message: `Map centered on: ${placeDetails.name || input}`,
            data: data,
          };
        }
      } catch (error) {
        console.error("❌ Error getting place details:", error);
      }
    }
    // If we have location directly, use it
    if (location) {
      try {
        const data: MapActionData = {
          coordinates: location,
          type: "move",
        };
        return {
          success: true,
          actionType: "map_action",
          message: `Map centered on address: ${input}`,
          data: data,
        };
      } catch (error) {
        console.error("❌ Error centering map on address:", error);
      }
    }

    // Fallback to server search
    return {
      success: false,
      actionType: "place_lookup",
      message: "Address search not found",
      shouldTriggerBackendSearch: false,
    };
  }

  /**
   * Execute text search action - trigger server search
   */
  private executeTextSearchAction(input: string): Promise<InputActionResult> {
    return Promise.resolve({
      success: true,
      actionType: "server_search",
      message: `${input}`,
      shouldTriggerBackendSearch: true,
    });
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Update input state (internal helper)
   */
  private updateState(updates: Partial<SearchInputState>): void {
    const currentState = this.inputStateSubject.value;
    this.inputStateSubject.next({ ...currentState, ...updates });
  }

  /**
   * Get history suggestions filtered by input and display mode
   */
  private getHistorySuggestions(input: string, displayMode: SearchDisplayMode): SearchSuggestion[] {
    const historySuggestions = this.searchHistory.getFilteredHistory(input, [], 4);

    // Filter by display mode compatibility
    return historySuggestions
      .filter((suggestion) => {
        const validation = this.modeCoordinator.canSuggest(suggestion.type, displayMode);
        return validation.valid;
      })
      .map((h) => {
        return {
          text: h.text,
          type: h.type,
          data: h.data,
          lastUsed: h.lastUsed,
          usageCount: h.usage,
        } as SearchSuggestion;
      });
  }

  /**
   * Create immediate suggestion for coordinate/WKT
   */
  private createImmediateSuggestion(detection: TypeDetectionResult, input: string): SearchSuggestion | null {
    const baseId = `immediate_${detection.inputType}_${Date.now()}`;

    if (detection.inputType === SearchInputType.COORDINATE && detection.parsedData?.coordinates) {
      return {
        id: baseId,
        text: input,
        type: SearchInputType.COORDINATE,
        confidence: detection.confidence * 100,
        data: {
          coordinates: {
            lat: detection.parsedData.coordinates[0],
            lng: detection.parsedData.coordinates[1],
          },
        },
      };
    }

    if (detection.inputType === SearchInputType.WKT_GEOMETRY && detection.parsedData?.wktGeometry) {
      return {
        id: baseId,
        text: input,
        type: SearchInputType.WKT_GEOMETRY,
        confidence: detection.confidence * 100,
        data: {
          geometry: {
            wkt: detection.parsedData.wktGeometry,
            boundingBox: detection.parsedData.boundingBox,
          },
        },
      };
    }

    return null;
  }

  /**
   * Create place suggestion from Google Places prediction
   */
  private createPlaceSuggestion(prediction: PlacePrediction, index: number, lowConfidence = false): SearchSuggestion {
    return {
      id: `place_${prediction.placeId}_${index}`,
      text: prediction.text,
      type: SearchInputType.GOOGLE_PLACE,
      confidence: lowConfidence ? 60 : 85,
      data: {
        address: {
          placeId: prediction.placeId,
          description: prediction.text,
          types: prediction.types,
        },
      },
    };
  }

  /**
   * Calculate confidence for address detection
   */
  private calculateAddressConfidence(predictions: PlacePrediction[], input: string): number {
    if (!predictions?.length) return 0;

    const topPrediction = predictions[0];
    const inputLower = input.toLowerCase();
    const predictionLower = topPrediction.text.toLowerCase();

    // Simple confidence calculation based on text similarity
    if (predictionLower.startsWith(inputLower)) {
      return 0.9;
    } else if (predictionLower.includes(inputLower)) {
      return 0.8;
    } else {
      return 0.6;
    }
  }

  /**
   * Parse coordinates from input text
   */
  private parseCoordinatesFromText(input: string): {
    lat: number;
    lng: number;
  } | null {
    const cleaned = input.replace(/[()]/g, "").trim();
    const parts = cleaned.split(/[,\s]+/).map((p) => parseFloat(p.trim()));

    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      const [first, second] = parts;

      // Determine lat/lng based on ranges
      if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
        return { lat: first, lng: second };
      } else if (Math.abs(second) <= 90 && Math.abs(first) <= 180) {
        return { lat: second, lng: first };
      }
    }

    return null;
  }
}
